import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, SimpleChanges } from '@angular/core';
import { getColorForValue } from './utils';

let uniqueGradientId = 0;

@Component({
  selector: 'area-graph',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      [attr.width]="width"
      [attr.height]="height"
      [attr.viewBox]="'0 0 ' + width + ' ' + height"
    >
      <!-- Area fill -->
      <path
        *ngIf="pathD"
        [attr.d]="pathD"
        [attr.fill]="'url(#' + gradientId + ')'"
        fill-rule="evenodd"
        clip-rule="evenodd"
        [attr.mask]="'url(#' + lineMaskId + ')'"
      />

      <!-- Dynamic horizontal gradient -->
      <defs>
        <mask [attr.id]="lineMaskId">
          <!-- Full white rectangle = fully visible -->
          <rect [attr.width]="width" [attr.height]="height" fill="white" />
          <!-- Black lines = fully transparent -->
          <line
            x1="1"
            [attr.y1]="height / 2"
            x2="49"
            [attr.y2]="height / 2"
            stroke="black"
            stroke-width="48"
            stroke-dasharray="1 8"
          />
        </mask>        
        <linearGradient
          [attr.id]="gradientId"
          x1="0"
          y1="0"
          [attr.x2]="width"
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop
            *ngFor="let stop of gradientStops"
            [attr.offset]="stop.offset"
            [attr.stop-color]="stop.color"
          />
        </linearGradient>
      </defs>
    </svg>
  `,
})
export class AreaGraphComponent {
  @Input() data: number[] = [];
  @Input() @HostBinding('style.width.px') width: number = 50;
  @Input() @HostBinding('style.height.px') height: number = 48;

  pathD = '';
  gradientStops: { offset: number; color: string }[] = [];
  uniqueGradientId = ++uniqueGradientId;
  gradientId = 'area-graph-gradient-' + uniqueGradientId;
  lineMaskId = 'line-mask-' + uniqueGradientId;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['width'] || changes['height']) {
      this.generatePath();
    }
  }

  generatePath(): void {
    if (!this.data || this.data.length < 2) {
      this.pathD = '';
      this.gradientStops = [];
      return;
    }

    const stepX = (this.width - 2) / (this.data.length - 1);
    const min = Math.min(...this.data);
    const max = Math.max(...this.data, 1); // avoid divide by zero

    const scaled = this.data.map(v => this.height - (v / max) * this.height);

    // Generate the smooth area path
    let d = `M0,${scaled[0]}`;
    for (let i = 1; i < scaled.length; i++) {
      const x = i * stepX;
      const xMid = (x + (i - 1) * stepX) / 2;
      const yMid = (scaled[i] + scaled[i - 1]) / 2;
      d += ` Q${(i - 1) * stepX},${scaled[i - 1]} ${xMid},${yMid}`;
    }

    // Close the shape with a natural edge
    const lastX = (this.data.length - 1) * stepX;
    const lastY = scaled[scaled.length - 1];
    d += ` L${lastX},${lastY}`;                // Follow actual point
    d += ` L${lastX},${this.height}`;          // Then drop to bottom
    d += ` L0,${this.height} Z`;               // Then back to start

    this.pathD = d;

    // Generate gradient stops at "turns"
    this.gradientStops = [];

    for (let i = 1; i < this.data.length - 1; i++) {
      const prev = this.data[i - 1];
      const curr = this.data[i];
      const next = this.data[i + 1];

      if ((curr > prev && curr > next) || (curr < prev && curr < next)) {
        const offset = (i / (this.data.length - 1)); // relative x-position
        const color = getColorForValue(0, curr, max);
        this.gradientStops.push({ offset, color });
      }
    }

    // Always add a start and end stop
    this.gradientStops.unshift({ offset: 0, color: getColorForValue(0, this.data[0], max) });
    this.gradientStops.push({ offset: 1, color: getColorForValue(0, this.data[this.data.length - 1], max) });
  }
}
